import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./BlogPost.css";
import { toAbsoluteUrl } from "../helpers/toAbsoluteUrl";
import { createBlogService } from "services/blogService";
import { GetBlogBySlugResponse } from "model/response/blog/getBlogBySlugResponse";
import { toast } from "react-toastify";
import Loading from "components/Loading/Loading";
import { AxiosError } from "axios";

const BlogPost = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const [post, setPost] = useState<GetBlogBySlugResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tooManyRequests, setTooManyRequests] = useState(false);

  const shouldShowBackButton = location.key !== "default";

  const blogService = createBlogService();

  const authorImages = {
    "LIVAD Automation": "livad.png",
    LIVAD: "livad.png",
    "Fatih Başkaya": "fatih-baskaya.png",
    "Arda Genç": "arda-genc.png",
    "Hasan Bera Aksay": "hasan-bera-aksay.png",
    "Emre Eskihoran": "emre-eskihoran.png",
  };

  const goBack = () => {
    navigate(-1);
  };

  const fetchBlogBySlug = async () => {
    if (!slug) {
      return;
    }
    try {
      setIsLoading(true);
      const response = await blogService.getBlogBySlug({
        slug: slug,
      });
      setPost(response);
      setTooManyRequests(false);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 429) {
          setTooManyRequests(true);
        }
      }
      console.error(error);
      toast.error("An error occurred while fetching blog by slug");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogBySlug();
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (isLoading) return;
    if (post) {
      document.title = `LIVAD Blog | ${post.title}`;
    } else if (!post && !tooManyRequests) {
      document.title = "LIVAD Blog | Not Found";
    } else if (tooManyRequests) {
      document.title = "LIVAD Blog | Too Many Requests";
    }
  }, [post, isLoading, tooManyRequests]);

  if (isLoading) {
    return (
      <div className="loading-container">
        <Loading />
      </div>
    );
  }

  if (!isLoading && tooManyRequests) {
    return (
      <div className="blog-post-container">
        <div className="post-not-found">
          <img
            src="/assets/429.png"
            alt="404"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
          />
          <p>You have made too many requests. Please try again later.</p>
        </div>
      </div>
    );
  }

  if (!isLoading && !post) {
    return (
      <div className="blog-post-container">
        <div className="post-not-found">
          <img
            src="/assets/404.png"
            alt="404"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
          />
          <p>The blog post you are looking for does not exist</p>
        </div>
      </div>
    );
  } else if (post) {
    return (
      <div className="blog-post-container">
        {shouldShowBackButton && (
          <button onClick={goBack} className="back-button">
            <svg
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              viewBox="0 0 512 512"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M401.4 224h-214l83-79.4c11.9-12.5 11.9-32.7 0-45.2s-31.2-12.5-43.2 0L89 233.4c-6 5.8-9 13.7-9 22.4v.4c0 8.7 3 16.6 9 22.4l138.1 134c12 12.5 31.3 12.5 43.2 0 11.9-12.5 11.9-32.7 0-45.2l-83-79.4h214c16.9 0 30.6-14.3 30.6-32 .1-18-13.6-32-30.5-32z"></path>
            </svg>{" "}
            Back
          </button>
        )}
        <article className="blog-post">
          <header className="post-header">
            <h1 className="post-title">{post.title}</h1>
            <div className="post-meta">
              <span className="post-author">
                <img
                  src={toAbsoluteUrl(
                    `/assets/authors/${
                      authorImages[post.author as keyof typeof authorImages]
                    }`,
                  )}
                  alt={post.author}
                  className="author-image"
                />
                {post.author}
              </span>
              <span className="post-date">
                {new Date(post.publishDate).toLocaleDateString()}
              </span>
            </div>
          </header>

          <div className="post-content">
            <div
              className="post-body"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>
        </article>
      </div>
    );
  }

  return null;
};

export default BlogPost;
